import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'

import { i18n, loadDomainMessages, loadShared } from './plugins/i18n'
import { useAccountStore } from './stores/account-store'

interface RouteModule {
	default: RouteRecordRaw | RouteRecordRaw[]
}

const domainModules = import.meta.glob<RouteModule>(
	'./domains/*/routes/index.ts',
	{
		eager: true
	}
)

const routes: RouteRecordRaw[] = Object.entries(domainModules).flatMap(
	([filePath, module]) => {
		const match = filePath.match(/\.\/domains\/([^/]+)\/routes\/index\.ts$/)
		if (!match) {
			return []
		}
		const domain = match[1]

		const defs = Array.isArray(module.default)
			? module.default
			: [module.default]

		return defs.map((route) => ({
			...route,
			meta: { ...(route.meta || {}), domain }
		}))
	}
)
const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
	scrollBehavior(to, from, savedPosition) {
		if (to.hash) {
			return {
				el: to.hash,
				behavior: 'smooth'
			}
		}

		if (savedPosition) {
			return { ...savedPosition, behavior: 'smooth' }
		}

		return { top: 0, behavior: 'smooth' }
	}
})

router.beforeEach(async (to, from, next) => {
	await loadShared(i18n.global.locale.value)

	const domain = to.meta.domain as string | undefined
	if (domain) {
		await loadDomainMessages(domain)
	}

	const accountStore = useAccountStore()

	// private routes guard
	const isPrivateRoute = !to.matched.some(
		(record) => record.meta.public || record.meta.publicOnly
	)
	if (isPrivateRoute && !accountStore.isLoggedIn) {
		next({
			name: 'AuthenticatePage',
			query: {
				to: to.fullPath
			}
		})
		return
	}

	// public only routes guard
	const isPublicOnlyRoute = to.matched.some((record) => record.meta.publicOnly)
	if (isPublicOnlyRoute && accountStore.isLoggedIn) {
		// TODO: goto account landing page
		next({ name: 'DashboardPage' })
		return
	}

	next()
})

router.afterEach(async (to) => {
	let title: Nullable<string> = null

	const { te, t } = i18n.global

	if (to.meta.title) {
		title = te(to.meta.title) ? t(to.meta.title) : to.meta.title
	}

	document.title = `${title || ''}${title ? ' - ' : ''}${t('shared.businessName')}`
})

export default router
